import type { z } from 'zod';

import {
  indexSearchSchema,
  listParams,
  type ListParams,
} from '@nucleus-commerce/admin-kit/index-table';

import type { GiftCardListParams } from '../api/gift-cards-api';
import { GIFT_CARD_FILTER_FIELDS, GIFT_CARD_SORTS } from '../components/gift-card-filters';

import { LAST4 } from './gift-card-rules';

/** URL search params of the gift cards list; mirrors GET /admin/v1/stores/{storeId}/gift-cards. */
export const giftCardListSearchSchema = indexSearchSchema(GIFT_CARD_FILTER_FIELDS, {
  sort: GIFT_CARD_SORTS,
});

export type GiftCardListSearch = z.infer<typeof giftCardListSearchSchema>;

/**
 * The endpoint has no `q`: the table's search box looks a card up by the last four characters of
 * its code. Anything but four letters or digits is not sent, so partial input keeps the list as
 * it is instead of answering 400.
 */
export function withLast4(params: ListParams): GiftCardListParams {
  const { q, ...rest } = params;
  const last4 = typeof q === 'string' ? q.trim() : '';
  return LAST4.test(last4) ? { ...rest, last4: last4.toUpperCase() } : rest;
}

/** The list request for a validated search; the page sends `withLast4(list.params)`, the same. */
export function giftCardListParams(search: GiftCardListSearch): GiftCardListParams {
  return withLast4(listParams(search, GIFT_CARD_FILTER_FIELDS));
}
